/**
 * PostHog loaded lazily after first render so it stays out of the main chunk (size budget).
 * Without `VITE_POSTHOG_KEY` it is a no-op (local default). Traffic goes through `/ph/*`
 * (proxy in the API), never directly. `distinct_id = lead_id` (ARCHITECTURE §10).
 *
 * Meta Pixel (`VITE_META_PIXEL_ID`, Tutlo's pixel) follows the same rules: lazy, no-op without
 * the id, skipped on `/admin`. The `fbq` stub queues calls until `fbevents.js` arrives, so
 * `trackContactCreated` is safe to call right after init. Both wait for Cookiebot consent
 * (`consent.ts`): without marketing consent `fbq` never exists and `lead_adults` is a no-op.
 * Exception: with `VITE_POSTHOG_COOKIELESS=on_reject` PostHog starts at once in cookieless mode
 * and switches to cookies only on statistics consent.
 *
 * Google Ads tag (`VITE_GOOGLE_ADS_ID`, Tutlo's account) loads on the same marketing consent, so it
 * runs Consent Mode v2 in basic mode: nothing reaches Google before the visitor agrees.
 */
import { onConsent } from './consent.ts'
import type { HeroChoice } from './hero/assign.ts'

type PostHog = typeof import('posthog-js').default

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[]
  push: Fbq
  loaded: boolean
  version: string
}

type Gtag = (...args: unknown[]) => void

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

let client: Promise<PostHog> | null = null
/**
 * Stamped on every PostHog event by `before_send` rather than `register`: registered properties
 * are lost when cookieless mode switches to cookies on consent, and the first `$pageview` can leave
 * before a register call lands.
 */
const superProps: Record<string, string | null> = {}
const pending: Array<[string, Record<string, unknown>]> = []
let pixelLoaded = false
let adsLoaded = false

/**
 * Conversion names live in code like `lead_adults`; the tag ids come from build args. The booking
 * event and the Google Ads labels wait for Tutlo's marketing (2026-10-19) — empty sends nothing.
 * Exported mutable for tests only.
 */
export const CONVERSIONS = {
  pixelLead: 'lead_adults',
  pixelBooking: '',
  adsLeadLabel: '',
  adsBookingLabel: '',
}

export function initAnalytics(): void {
  // The admin panel is not part of the funnel — its traffic would pollute conversion metrics.
  // Nor is the hero preview (/podglad): it renders versions without a visit behind them.
  if (/^\/(admin|podglad)/.test(window.location.pathname)) return
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined
  const pixelId = import.meta.env.VITE_META_PIXEL_ID as string | undefined
  const adsId = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined
  if (!key && !pixelId && !adsId) return
  const cookieless = import.meta.env.VITE_POSTHOG_COOKIELESS === 'on_reject'
  const loadPostHog = (key: string) => {
    client ??= import('posthog-js').then(({ default: posthog }) => {
      posthog.init(key, {
        api_host: '/ph',
        ui_host: 'https://eu.posthog.com',
        persistence: 'localStorage+cookie',
        capture_pageview: true,
        autocapture: false,
        before_send: (event) => {
          if (event) event.properties = { ...superProps, ...event.properties }
          return event
        },
        // Until the visitor accepts statistics PostHog writes nothing to the device and identifies
        // them by a server-side daily hash. Needs cookieless mode switched on in the project settings,
        // otherwise PostHog drops these events.
        ...(cookieless
          ? { cookieless_mode: 'on_reject' as const, opt_out_capturing_by_default: true }
          : {}),
      })
      for (const [event, props] of pending.splice(0)) posthog.capture(event, props)
      return posthog
    })
  }
  // Cookieless PostHog does not wait for the banner: most visitors never answer it.
  if (key && cookieless) idle(() => loadPostHog(key))
  // Consent first (Cookiebot): statistics → PostHog, marketing → Meta Pixel and Google Ads. Each loads once, on the
  // first consent that allows it. Cookiebot does not reload the SPA when consent is withdrawn, so a
  // later change switches the already loaded trackers off and on in place.
  onConsent((consent) => {
    const load = () => {
      if (key && consent.statistics) loadPostHog(key)
      client
        ?.then((posthog) => {
          if (cookieless) {
            // Opting out stores a flag on the device, so only a visitor who had opted in is opted out.
            const status = posthog.get_explicit_consent_status()
            if (consent.statistics && status !== 'granted') posthog.opt_in_capturing()
            else if (!consent.statistics && status === 'granted') posthog.opt_out_capturing()
            return
          }
          // PostHog persists an opt-out, so a returning visitor who consents again must be opted back in.
          if (!consent.statistics) posthog.opt_out_capturing()
          else if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing()
        })
        .catch(() => {})
      if (pixelId && consent.marketing && !pixelLoaded) {
        pixelLoaded = true
        initPixel(pixelId)
      } else if (pixelLoaded) {
        window.fbq?.('consent', consent.marketing ? 'grant' : 'revoke')
      }
      if (adsId && consent.marketing && !adsLoaded) {
        adsLoaded = true
        initGoogleAds(adsId)
      } else if (adsLoaded) {
        window.gtag?.('consent', 'update', adsConsent(consent.marketing))
      }
    }
    idle(load)
  })
}

function idle(fn: () => void): void {
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn)
  else setTimeout(fn, 1)
}

/** Meta's official bootstrap snippet, minus the IIFE: a queueing stub, then the async script. */
function initPixel(pixelId: string): void {
  if (window.fbq) return
  const fbq: Fbq = Object.assign(
    (...args: unknown[]) => {
      if (fbq.callMethod) fbq.callMethod(...args)
      else fbq.queue.push(args)
    },
    { queue: [] as unknown[], loaded: true, version: '2.0' },
  ) as Fbq
  fbq.push = fbq
  window.fbq = fbq
  window._fbq = fbq
  const script = document.createElement('script')
  script.async = true
  // Consent was checked by us; keep Cookiebot's auto blocker from holding this tag back again.
  script.dataset.cookieconsent = 'ignore'
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
  fbq('init', pixelId)
  fbq('track', 'PageView')
}

const adsConsent = (granted: boolean) => {
  const value = granted ? 'granted' : 'denied'
  return { ad_storage: value, ad_user_data: value, ad_personalization: value }
}

/** Google's gtag.js snippet: a `dataLayer` queue first, then the async script. */
function initGoogleAds(adsId: string): void {
  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag() {
    // gtag.js only reads the `arguments` object; a pushed rest-args array is silently ignored.
    // biome-ignore lint/complexity/noArguments: required by gtag.js
    window.dataLayer?.push(arguments)
  }
  window.gtag('consent', 'default', adsConsent(true))
  window.gtag('js', new Date())
  window.gtag('config', adsId)
  const script = document.createElement('script')
  script.async = true
  script.dataset.cookieconsent = 'ignore'
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(adsId)}`
  document.head.appendChild(script)
}

function adsConversion(label: string): void {
  const adsId = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined
  if (!adsId || !label) return
  window.gtag?.('event', 'conversion', { send_to: `${adsId}/${label}` })
}

/**
 * Media opened on /konto and in the waiting room. The players live in cross-origin iframes the
 * replay cannot see, so taps on them showed up as dead clicks without this.
 */
export function trackMediaOpened(media: 'konto_video' | 'meeting_demo'): void {
  client?.then((posthog) => posthog.capture('media_opened', { media })).catch(() => {})
}

export function identifyLead(leadId: string): void {
  client?.then((posthog) => posthog.identify(leadId)).catch(() => {})
}

/**
 * Tutlo's conversion for a new contact (e-mail saved, account created). Event name and the
 * `track` call are 1:1 with Tutlo's own funnel (Tutlo, 2026-09-25), so their Meta campaigns
 * keep optimising on the same event. Not for test leads: they would skew the ad account.
 */
export function trackContactCreated(lead: { isTest: boolean }): void {
  if (lead.isTest) return
  window.fbq?.('track', CONVERSIONS.pixelLead)
  adsConversion(CONVERSIONS.adsLeadLabel)
}

/** A new booking (form or chat), never a reschedule. */
export function trackBooked(lead: { isTest: boolean }): void {
  if (lead.isTest) return
  if (CONVERSIONS.pixelBooking) window.fbq?.('track', CONVERSIONS.pixelBooking)
  adsConversion(CONVERSIONS.adsBookingLabel)
}

/** Which landing button started the visit: the funnel in Postgres only knows that one was used. */
export function trackHookCta(cta: 'card' | 'slot' | 'bar' | 'chat' | 'bar_chat'): void {
  client?.then((posthog) => posthog.capture('hook_cta_clicked', { cta })).catch(() => {})
}

/**
 * A day without hours in the day picker: the tap itself (it used to be a dead click on a disabled
 * chip) and the jump to the next free slot from the empty state. `holiday` = Polish public holiday,
 * `not_open` = past the booking window, `no_slots` = inside it but fully booked.
 */
export function trackUnavailableDay(
  action: 'clicked' | 'show_nearest',
  props: { day: string; reason: 'sunday' | 'holiday' | 'not_open' | 'no_slots'; target?: string },
): void {
  client
    ?.then((posthog) => posthog.capture('unavailable_day', { action, ...props }))
    .catch(() => {})
}

/** Tap on the „Najbliższa lekcja już za … minut” banner in the day picker, which jumps to that slot. */
export function trackNearestBanner(props: { minutes_until: number; from_day: string }): void {
  client?.then((posthog) => posthog.capture('nearest_banner_clicked', props)).catch(() => {})
}

let heroShown: string | null = null

/** Called as soon as the hero is chosen (route loader), before PostHog may send its first pageview. */
export function setHeroContext(choice: HeroChoice) {
  const props = {
    hero_version: choice.version,
    ab_test: choice.test,
    ab_variant: choice.variant,
    ab_assignment: choice.assignment,
  }
  Object.assign(superProps, props)
  return props
}

/**
 * Which hero `/` showed and why: super properties on every later PostHog event (pageviews, CTA
 * clicks, the funnel pages, replays) plus one `hero_shown` per page load — the exposure event the
 * per-arm PostHog funnels start from. Works before PostHog loads and in cookieless mode.
 */
export function trackHeroShown(choice: HeroChoice): void {
  const props = setHeroContext(choice)
  const key = JSON.stringify(props)
  if (heroShown === key) return
  heroShown = key
  capture('hero_shown', props)
}

/** Events fired before PostHog loads (it starts lazily) wait here instead of being dropped. */
function capture(event: string, props: Record<string, unknown>): void {
  if (!client) {
    pending.push([event, props])
    return
  }
  client.then((posthog) => posthog.capture(event, props)).catch(() => {})
}
